import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, MailOpen, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/enquiries").then(({ data }) => setItems(data || [])).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { document.title = "Enquiries | Aimway Admin"; load(); }, []);

  const toggleRead = async (e) => {
    try {
      await api.patch(`/admin/enquiries/${e.id}`, { read: !e.read });
      setItems((p) => p.map((x) => (x.id === e.id ? { ...x, read: !e.read } : x)));
    } catch { toast.error("Update failed"); }
  };

  const remove = async (e) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/enquiries/${e.id}`);
      setItems((p) => p.filter((x) => x.id !== e.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const open = (e) => {
    setView(e);
    if (!e.read) toggleRead(e);
  };

  return (
    <div data-testid="admin-enquiries">
      <h1 className="font-display text-2xl font-800 text-brand-navy">Enquiries</h1>
      <p className="mt-1 text-sm text-muted-foreground">Messages submitted through your website forms.</p>

      <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No enquiries yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 font-600">Name</th>
                  <th className="px-5 py-3 font-600">Contact</th>
                  <th className="px-5 py-3 font-600">Interest</th>
                  <th className="px-5 py-3 font-600">Date</th>
                  <th className="px-5 py-3 text-right font-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className={`border-b border-slate-50 last:border-0 hover:bg-slate-50/60 ${!e.read ? "bg-brand-sky/40" : ""}`} data-testid="enquiry-row">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 font-600 text-brand-navy">
                        {!e.read && <span className="h-2 w-2 rounded-full bg-brand-orange" />}
                        {e.name}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{e.email}<br /><span className="text-xs text-muted-foreground">{e.phone}</span></td>
                    <td className="px-5 py-3"><span className="rounded-full bg-brand-sky px-2.5 py-0.5 text-xs font-600 text-brand">{e.interest}</span></td>
                    <td className="px-5 py-3 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => open(e)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-brand hover:bg-brand-sky" title="View" data-testid="enquiry-view"><Eye size={15} /></button>
                        <button onClick={() => toggleRead(e)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title={e.read ? "Mark unread" : "Mark read"}>{e.read ? <MailOpen size={15} /> : <Mail size={15} />}</button>
                        <button onClick={() => remove(e)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-red-500 hover:bg-red-50" title="Delete" data-testid="enquiry-delete"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-brand-navy">{view?.interest}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3 py-2 text-sm">
              <Row k="Name" v={view.name} />
              <Row k="Email" v={<a href={`mailto:${view.email}`} className="text-brand">{view.email}</a>} />
              <Row k="Phone" v={<a href={`tel:${view.phone}`} className="text-brand">{view.phone}</a>} />
              <Row k="Company" v={view.company || "—"} />
              <Row k="Message" v={<span className="whitespace-pre-wrap">{view.message || "—"}</span>} />
              <Row k="Received" v={new Date(view.created_at).toLocaleString()} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex gap-3">
    <div className="w-24 shrink-0 text-muted-foreground">{k}</div>
    <div className="font-500 text-brand-navy">{v}</div>
  </div>
);
